import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { Home, Sparkles, FileText, Inbox, Menu, Mic } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import useVoiceTranscribe from '../../hooks/useVoiceTranscribe';
import { cn } from '../../lib/utils';

/**
 * Bottom navigation bar for phones (< lg).
 *
 * Surfaces the 4 most-used destinations + a "More" trigger that opens the
 * full sidebar drawer. Lives outside the main content flow so it doesn't
 * push other UI; AppLayout adds `pb-16 lg:pb-0` to its main wrapper so
 * content isn't hidden behind it.
 */
export default function MobileBottomNav({ onOpenSidebar, unreadReplies = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();

  // ─── Voice → Quick Buck: long-press the AI Hub bucket to dictate ───────
  const holdTimerRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const transcriptRef = useRef('');
  const { start: startRec, stop: stopRec, busy: voiceBusy } = useVoiceTranscribe({
    onText: (text) => {
      // Whisper returns chunks — accumulate.
      transcriptRef.current = (transcriptRef.current + ' ' + text).trim();
      setTranscript(transcriptRef.current);
    },
  });

  const beginHold = (e) => {
    // Prevent the surrounding <Link> from navigating immediately — we'll
    // either dictate (long press) or fall through to navigate (short tap).
    if (e?.preventDefault) e.preventDefault();
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(async () => {
      holdTimerRef.current = null;
      transcriptRef.current = '';
      setTranscript('');
      setRecording(true);
      toast.message('Recording — release to send to AI Hub', { duration: 1500 });
      await startRec({ continuous: false });
    }, 420);
  };

  const endHold = () => {
    if (holdTimerRef.current) {
      // Short tap: cancel the pending long-press and navigate normally.
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
      navigate('/ai-hub');
      return;
    }
    // Long-press release: stop recording and (once whisper finishes)
    // route to AI Hub with the transcript prefilled.
    if (recording) {
      stopRec();
      setRecording(false);
      // Wait one tick for whisper inflight to settle, then route.
      const handoff = () => {
        const text = transcriptRef.current?.trim();
        if (!text) {
          toast.error("Didn't catch that — try holding a bit longer.");
          return;
        }
        try { sessionStorage.setItem('ai_studio_prefill_prompt', text); } catch { /* ignore */ }
        // autoSend=1 tells AI Hub to immediately fire sendMessage so the
        // dictated command flows straight into the intent router.
        navigate('/ai-hub?prefill=1&autoSend=1');
      };
      // Whisper transcribe runs in the background; we poll briefly so even
      // 1-2s clips get a chance to land before we hand off.
      let tries = 0;
      const poll = setInterval(() => {
        tries += 1;
        if (transcriptRef.current || tries > 20) {
          clearInterval(poll);
          handoff();
        }
      }, 250);
    }
  };

  const items = [
    { path: '/dashboard', label: 'Home', icon: Home, match: (p) => p === '/' || p.startsWith('/dashboard') },
    { path: '/ai-hub', label: 'AI Hub', icon: Sparkles, match: (p) => p.startsWith('/ai-hub') },
    { path: '/documents', label: 'Docs', icon: FileText, match: (p) => p.startsWith('/documents') },
    {
      path: '/inbox',
      label: 'Inbox',
      icon: Inbox,
      match: (p) => p.startsWith('/inbox') || p.startsWith('/client-replies') || p.startsWith('/messages'),
      badge: unreadReplies,
    },
  ];

  return (
    <>
      {/* Live transcript bubble shown above the nav while dictating */}
      {(recording || voiceBusy || transcript) && (
        <div
          className="lg:hidden fixed bottom-20 left-0 right-0 z-40 px-4 pointer-events-none"
          data-testid="mobile-nav-voice-bubble"
        >
          <div className="mx-auto max-w-sm bg-zinc-900/95 dark:bg-zinc-800/95 text-white rounded-2xl shadow-2xl backdrop-blur-xl px-4 py-3 border border-emerald-500/30">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-semibold mb-1.5">
              {recording ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  Recording
                </>
              ) : voiceBusy ? (
                <>
                  <Mic className="w-3 h-3" /> Transcribing…
                </>
              ) : (
                <>
                  <Mic className="w-3 h-3" /> Captured
                </>
              )}
            </div>
            <p className="text-sm leading-snug min-h-[1.25rem]">
              {transcript || (
                <span className="italic text-zinc-400">Start speaking…</span>
              )}
            </p>
          </div>
        </div>
      )}
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 h-16 bg-background/95 backdrop-blur-xl border-t border-border"
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-5 h-full">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.match(location.pathname);
          const slug = item.label.toLowerCase().replace(/\s+/g, '-');

          // AI Hub bucket has special long-press behaviour (voice → AI prefill).
          // Render it as a <button> rather than <Link> so we can fully control
          // touch / pointer events without React Router intercepting taps.
          if (item.path === '/ai-hub') {
            const isHotMic = recording || voiceBusy;
            return (
              <button
                key={item.path}
                type="button"
                onPointerDown={beginHold}
                onPointerUp={endHold}
                onPointerLeave={() => {
                  if (recording) endHold();
                  else if (holdTimerRef.current) {
                    clearTimeout(holdTimerRef.current);
                    holdTimerRef.current = null;
                  }
                }}
                onContextMenu={(e) => e.preventDefault()}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 transition-colors relative select-none touch-none',
                  active || isHotMic
                    ? 'text-emerald-500 dark:text-emerald-400'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                data-testid={`mobile-nav-${slug}`}
                aria-label="AI Hub — tap to open, hold to dictate"
              >
                {(active || isHotMic) && (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b',
                      isHotMic ? 'bg-red-500 animate-pulse' : 'bg-emerald-500',
                    )}
                  />
                )}
                <div className="relative">
                  {isHotMic ? (
                    <Mic className="w-5 h-5 text-red-500 animate-pulse" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-[10px] font-medium">
                  {isHotMic ? 'Listening…' : item.label}
                </span>
              </button>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5 transition-colors relative',
                active
                  ? 'text-emerald-500 dark:text-emerald-400'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              data-testid={`mobile-nav-${slug}`}
            >
              {active && (
                <span
                  aria-hidden
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-emerald-500 rounded-b"
                />
              )}
              <div className="relative">
                <Icon className="w-5 h-5" />
                {item.badge > 0 && (
                  <Badge
                    className="absolute -top-2 -right-3 min-w-[16px] h-4 px-1 text-[9px] font-semibold bg-emerald-500 text-white border-0"
                    data-testid={`mobile-nav-${slug}-badge`}
                  >
                    {item.badge > 9 ? '9+' : item.badge}
                  </Badge>
                )}
              </div>
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onOpenSidebar}
          className="flex flex-col items-center justify-center gap-0.5 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="mobile-nav-more"
          aria-label="Open full menu"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px] font-medium">More</span>
        </button>
      </div>
    </nav>
    </>
  );
}
